import styled from '@emotion/styled'
import { AnimatePresence, m } from 'framer-motion'

import { TickIcon, WarningTriangleIcon } from 'components/icons'

const Container = styled(m.div)<{ isTitlePresent: boolean; type: 'success' | 'warning' }>`
	display: flex;
	flex-direction: ${({ isTitlePresent }) => (isTitlePresent ? 'column' : 'row')};
	padding: ${({ theme, isTitlePresent }) =>
		isTitlePresent ? `${theme.spaces.space3} ${theme.spaces.space3} ${theme.spaces.space6}` : `${theme.spaces.space3}`};
	background-color: ${({ theme, type }) => (type === 'success' ? '#e0f5df' : theme.colors.light.warningSurface)};
	border-radius: ${({ theme }) => theme.borderRadiuses.lg};
	margin-bottom: ${({ theme }) => theme.spaces.space5};
`

const IconWrapper = styled.span<{ type: 'success' | 'warning' }>`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	margin-right: ${({ theme, type }) => (type === 'success' ? theme.spaces.space3 : '0')};
	color: ${({ theme, type }) => (type === 'success' ? theme.colors.light.surface : theme.colors.light.warningPrimary)};
	background-color: ${({ type }) => (type === 'success' ? '#1cab00' : 'transparent')};
	border-radius: ${({ theme, type }) => (type === 'success' ? theme.borderRadiuses.round : 'none')};
	width: ${({ type }) => (type === 'success' ? '24px' : 'auto')};
	height: ${({ type }) => (type === 'success' ? '24px' : 'auto')};
`

const SuccessIcon = styled(TickIcon)`
	margin-right: ${({ theme }) => theme.spaces.space3};
	width: 24px;
	height: 24px;
`

const WarningIcon = styled(WarningTriangleIcon)`
	color: ${({ theme }) => theme.colors.light.warningPrimary};
	margin-right: ${({ theme }) => theme.spaces.space3};
`

const ContentWrapper = styled.div<{ isTitlePresent: boolean }>`
	display: flex;
	flex-direction: column;
	margin-left: ${({ isTitlePresent, theme }) => (isTitlePresent ? theme.spaces.space5 : 0)};
`

const TitleWrapper = styled.div`
	display: flex;
	padding: ${({ theme }) => theme.spaces.space2};
`

const Title = styled.p`
	${({ theme }) => theme.typography.captionMedium};
	margin: 0;
`

const Content = styled.p<{ isTitlePresent: boolean }>`
	${({ theme }) => theme.typography.caption};
	margin: ${({ isTitlePresent, theme }) => (isTitlePresent ? `6px 0 0 ${theme.spaces.space7}` : 0)};
`

const TitleOuter = styled.div`
	display: flex;
	align-items: end;
	padding-bottom: ${({ theme }) => theme.spaces.space2};
`

type Props = {
	title?: string
	isVisible: boolean
	scrollIntoView?: boolean
	children: React.ReactNode
	type: 'success' | 'warning'
}

function StatusBanner({ title, children, isVisible, scrollIntoView = false, type }: Props) {
	const isTitlePresent = Boolean(title)
	return (
		<AnimatePresence>
			{isVisible && (
				<Container
					initial={{ y: '-50%', opacity: 0 }}
					animate={{ y: 0, opacity: 1 }}
					isTitlePresent={isTitlePresent}
					type={type}
					ref={(ref) => {
						if (ref && scrollIntoView) {
							ref.scrollIntoView({ behavior: 'smooth' })
						}
					}}
					data-testid={`${type}-banner`}
				>
					<TitleWrapper>
						<IconWrapper type={type}>{type === 'success' ? <SuccessIcon /> : <WarningIcon />}</IconWrapper>
						{isTitlePresent && (
							<TitleOuter>
								<Title>{title}</Title>
							</TitleOuter>
						)}
					</TitleWrapper>
					<ContentWrapper isTitlePresent={isTitlePresent}>
						<Content isTitlePresent={isTitlePresent}>{children}</Content>
					</ContentWrapper>
				</Container>
			)}
		</AnimatePresence>
	)
}

export default StatusBanner
